<template>
	<div class="ci-consolidation">
		<header class="cons-head">
			<p class="cons-eyebrow">Consolidación</p>
			<h2 class="cons-title">Miembros, cuentas ignoradas y neteos intercompañía</h2>
			<p class="cons-hint">
				La consolidada suma los documentos de sus miembros. Acá eliges qué cuentas quedan
				fuera del reporte y qué pares de cuentas se netean entre empresas; el detalle de
				cada neteo se ve en esta pestaña y el reporte muestra solo la línea
				«Ajustes intercompañía».
			</p>
		</header>

		<div class="cons-cards">
			<ConsolidationCard
				eyebrow="Miembros"
				title="Empresas que forman la consolidada"
				:summary="countLabel(members.length, 'empresa', 'empresas')"
			>
				<ConsolidatedMembersTable
					:members="members"
					:loading="loading"
					@manage-members="openMembersModal"
				/>
			</ConsolidationCard>

			<ConsolidationCard
				v-if="members.length"
				eyebrow="Cuentas ignoradas"
				title="Cuentas que quedan fuera del reporte consolidado"
				:summary="countLabel(ignoredCount, 'cuenta', 'cuentas')"
			>
				<ConsolidatedIgnoredAccounts
					ref="ignored"
					:members="members"
					:ignoredByMember="ignoredByMember"
					:optionsByMember="ignoreOptionsByMember"
					:saving="saving"
					@load-options="loadIgnoreOptions"
					@save="saveIgnored"
				/>
			</ConsolidationCard>

			<ConsolidationCard
				v-for="kind in nettingKinds"
				:key="kind"
				eyebrow="Neteos intercompañía"
				:title="nettingTitle(kind)"
				:summary="countLabel(nettingsOf(kind).length, 'neteo', 'neteos')"
				default-open
			>
				<ConsolidatedNettingEditor
					:ref="'netting-' + kind"
					:kind="kind"
					:nettings="nettingsOf(kind)"
					:options="nettingOptions[kind]"
					:preview="previewKind === kind ? preview : null"
					:blockTotal="blockTotal(kind)"
					:saving="saving"
					@load-options="loadNettingOptions"
					@preview="previewNetting"
					@save="saveNetting"
					@delete="deleteNetting"
					@cancel="clearPreview"
				/>
			</ConsolidationCard>
		</div>
	</div>
</template>

<script>
// Pestaña «Consolidación» de la ficha (plan consolidadas, D8): solo para
// empresas consolidadas. La lista (ajustes + resumen por miembro) vive en el
// módulo consolidatedAdjustments; el modal de miembros sigue siendo el único
// camino para agregar o quitar empresas.
import { mapActions, mapGetters, mapState } from "vuex";
import ConsolidatedIgnoredAccounts from "@/components/companyInfo/ConsolidatedIgnoredAccounts";
import ConsolidatedMembersTable from "@/components/companyInfo/ConsolidatedMembersTable";
import ConsolidatedNettingEditor from "@/components/companyInfo/ConsolidatedNettingEditor";
import ConsolidationCard from "@/components/companyInfo/ConsolidationCard";
import { KIND_NETTING_BALANCE, KIND_NETTING_RESULTS } from "@/store/consolidatedAdjustments";
import ManageConsolidatedMembers from "@/components/modals/ManageConsolidatedMembers";
import { consoleHandlerError, extractErrorMessage, notificate } from "@/utils/message";

export default {
	name: "CompanyInfoConsolidation",
	components: {
		ConsolidatedIgnoredAccounts,
		ConsolidatedMembersTable,
		ConsolidatedNettingEditor,
		ConsolidationCard,
	},
	props: {
		basicData: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			// { [member_company_id]: [{ id, label, level_1 }] } para el selector
			// de ignoradas; se pide al abrir la edición de cada miembro.
			ignoreOptionsByMember: {},
			nettingKinds: [KIND_NETTING_BALANCE, KIND_NETTING_RESULTS],
			// { [kind]: { left: [...], right: [...] } } del endpoint de opciones.
			nettingOptions: {
				[KIND_NETTING_BALANCE]: { left: [], right: [] },
				[KIND_NETTING_RESULTS]: { left: [], right: [] },
			},
			// A qué bloque pertenece el preview vigente del store.
			previewKind: null,
		};
	},
	computed: {
		...mapState("consolidatedAdjustments", ["members", "loading", "saving", "preview"]),
		...mapGetters("consolidatedAdjustments", [
			"blockTotal",
			"ignoreAdjustments",
			"ignoredByMember",
			"balanceNettings",
			"resultsNettings",
		]),
		companyId() {
			return this.basicData.companyId;
		},
		ignoredCount() {
			return Object.values(this.ignoredByMember).reduce((n, lines) => n + lines.length, 0);
		},
	},
	watch: {
		companyId: {
			handler(id) {
				if (id) this.load();
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions("consolidatedAdjustments", [
			"fetchAdjustments",
			"fetchAccountOptions",
			"previewAdjustment",
			"clearPreview",
			"saveAdjustment",
			"deleteAdjustment",
		]),
		countLabel(count, singular, plural) {
			return `${count} ${count === 1 ? singular : plural}`;
		},
		nettingTitle(kind) {
			return kind === KIND_NETTING_BALANCE
				? "Balance: cuentas por cobrar y por pagar entre miembros"
				: "Resultados: ventas de un miembro contra costos de otro";
		},
		nettingsOf(kind) {
			return kind === KIND_NETTING_BALANCE ? this.balanceNettings : this.resultsNettings;
		},
		nettingEditor(kind) {
			const ref = this.$refs[`netting-${kind}`];
			return Array.isArray(ref) ? ref[0] : ref;
		},
		async loadNettingOptions({ kind, adjustmentId }) {
			try {
				const [left, right] = await Promise.all(
					["left", "right"].map((side) =>
						this.fetchAccountOptions({ kind, side, adjustmentId }),
					),
				);
				this.nettingOptions = { ...this.nettingOptions, [kind]: { left, right } };
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudieron cargar las cuentas de los miembros."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async previewNetting({ kind, lines, adjustmentId = null }) {
			try {
				await this.previewAdjustment({ kind, lines, adjustmentId });
				this.previewKind = kind;
			} catch (error) {
				// Informativo: un preview que falla no bloquea el guardado.
				this.clearPreview();
				consoleHandlerError({ error, silent: true });
			}
		},
		async saveNetting({ kind, label, lines, adjustmentId }) {
			try {
				await this.saveAdjustment({ kind, label, lines, adjustmentId });
				notificate("Neteo guardado.", "is-success is-light");
				this.previewKind = null;
				const editor = this.nettingEditor(kind);
				if (editor) editor.close();
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudo guardar el neteo."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async deleteNetting(netting) {
			try {
				await this.deleteAdjustment(netting.id);
				notificate("Neteo eliminado.", "is-success is-light");
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudo eliminar el neteo."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		// El ajuste `ignore` que ya contiene cuentas de ese miembro (la UI
		// mantiene uno por miembro; si otro camino mezcló miembros, se
		// conservan las líneas ajenas al reemplazar).
		ignoreAdjustmentFor(memberId) {
			return this.ignoreAdjustments.find(
				(adjustment) => adjustment.lines.some((line) => line.member_company_id === memberId),
			) || null;
		},
		async loadIgnoreOptions(memberId) {
			const current = this.ignoreAdjustmentFor(memberId);
			try {
				const rows = await this.fetchAccountOptions({
					kind: "ignore",
					adjustmentId: current ? current.id : null,
				});
				const member = rows.find((row) => row.company_id === memberId);
				this.ignoreOptionsByMember = {
					...this.ignoreOptionsByMember,
					[memberId]: (member ? member.accounts : []).map((account) => ({
						id: account.id,
						label: `${account.level_3} · ${account.level_2}`,
						level_1: account.level_1,
					})),
				};
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudieron cargar las cuentas del miembro."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async saveIgnored({ memberId, accountIds }) {
			const current = this.ignoreAdjustmentFor(memberId);
			const others = current
				? current.lines.filter((line) => line.member_company_id !== memberId)
				: [];
			const lines = [
				...others.map((line) => ({
					member_company_id: line.member_company_id,
					account_id: line.account_id,
					side: "left",
				})),
				...accountIds.map((accountId) => ({
					member_company_id: memberId,
					account_id: accountId,
					side: "left",
				})),
			];
			try {
				if (lines.length === 0 && current) {
					await this.deleteAdjustment(current.id);
				} else if (lines.length > 0) {
					await this.saveAdjustment({
						kind: "ignore",
						label: "",
						lines,
						adjustmentId: current ? current.id : null,
					});
				}
				notificate("Cuentas ignoradas guardadas.", "is-success is-light");
				if (this.$refs.ignored) this.$refs.ignored.close();
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudieron guardar las cuentas ignoradas."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async load() {
			try {
				await this.fetchAdjustments(this.companyId);
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudo cargar la consolidación de la empresa."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		// Mismo modal que el panel de cartera; al cerrarlo la lista y la ficha
		// se recargan (otro conjunto de miembros = otras cuentas elegibles).
		openMembersModal() {
			const reload = () => {
				this.load();
				this.$emit("members-changed");
			};
			this.$buefy.modal.open({
				parent: this,
				hasModalCard: true,
				customClass: "modal-class",
				canCancel: ["escape", "outside"],
				component: ManageConsolidatedMembers,
				props: {
					consolidatedId: this.companyId,
					consolidatedName: this.basicData.company_name || "",
				},
				events: { close: reload },
				onCancel: reload,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.ci-consolidation {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.cons-cards {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.cons-eyebrow {
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.cons-title {
	margin: 0;
	font-size: 17px;
	font-weight: 700;
	color: var(--ap-ink);
}

.cons-hint {
	margin: 8px 0 0;
	max-width: 70ch;
	font-size: 13.5px;
	line-height: 1.5;
	color: var(--ap-slate);
}
</style>
